/**
 * Scroll-position restoration across SPA navigation.
 *
 * Default behaviour: every page scrolls to the top on mount.
 *
 * One exception: OverviewPage (the spots list within a region) restores
 * the user's previous scroll position if they came back from a spot
 * detail page. The flow we're optimising for is:
 *
 *   1. Open /region/stockholm, scroll down 800px, click a spot card
 *   2. Land on /spot/abc, browse, hit back
 *   3. Land on /region/stockholm at scrollY=800 (not the top)
 *
 * Going to any *other* page (regions home, how-it-works, etc.) and
 * back to the overview should still reset to the top — we only
 * restore when the previous route was a /spot/:id.
 *
 * State lives in sessionStorage so it survives wouter's pushState
 * navigations but doesn't bleed across browser tabs.
 */

import { useEffect } from "react";

const PREV_LOCATION_KEY = "__bw:prevLocation";
const OVERVIEW_SCROLL_PREFIX = "__bw:overviewScroll:";

// ── Storage helpers ─────────────────────────────────────────────────────────

function getPrevLocation(): string | null {
  if (typeof window === "undefined") return null;
  return sessionStorage.getItem(PREV_LOCATION_KEY);
}
function setPrevLocation(loc: string): void {
  if (typeof window === "undefined") return;
  sessionStorage.setItem(PREV_LOCATION_KEY, loc);
}

function overviewScrollKey(regionId: string): string {
  return `${OVERVIEW_SCROLL_PREFIX}${regionId}`;
}
function getOverviewScroll(regionId: string): number | null {
  if (typeof window === "undefined") return null;
  const saved = sessionStorage.getItem(overviewScrollKey(regionId));
  return saved == null ? null : Number(saved);
}
function setOverviewScroll(regionId: string, y: number): void {
  if (typeof window === "undefined") return;
  sessionStorage.setItem(overviewScrollKey(regionId), String(y));
}

/**
 * Re-attempt scrollTo a couple of times over the first ~half second
 * after mount. Catches the case where the page mounts with empty
 * content (data still loading) — the first call sets scrollY=0 because
 * the document is too short, then a second call after content fills
 * lands on the right position.
 */
function restoreWithRetries(y: number): void {
  window.scrollTo(0, y);
  for (const delay of [50, 200, 500]) {
    setTimeout(() => {
      if (Math.abs(window.scrollY - y) > 4 && document.body.scrollHeight >= y) {
        window.scrollTo(0, y);
      }
    }, delay);
  }
}

// ── Hooks ───────────────────────────────────────────────────────────────────

/**
 * Default scroll behaviour for any page: jump to the top on mount and
 * record this page as the "previous location" so the *next* page knows
 * where the user came from.
 */
export function useScrollToTopOnMount(currentLocation: string): void {
  useEffect(() => {
    window.scrollTo(0, 0);
    setPrevLocation(currentLocation);
  }, [currentLocation]);
}

/**
 * Special-case for OverviewPage: restore scroll position if the user
 * just navigated back from a spot detail. Otherwise scroll to top.
 *
 * Continuously saves scroll position (throttled via rAF) while on the
 * page so the latest scroll is what gets restored.
 */
export function useOverviewScrollRestore(
  regionId: string,
  currentLocation: string,
): void {
  // Restore on mount + register this page as the new prev location.
  useEffect(() => {
    const prev = getPrevLocation();
    if (prev?.startsWith("/spot/")) {
      const saved = getOverviewScroll(regionId);
      if (saved != null) {
        restoreWithRetries(saved);
      } else {
        window.scrollTo(0, 0);
      }
    } else {
      window.scrollTo(0, 0);
    }
    setPrevLocation(currentLocation);
    // Restoring once is enough — any later navigation away will set a
    // fresh prevLocation, and the user's scroll position keeps updating
    // via the listener below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentLocation, regionId]);

  // Continuously save scroll position. rAF debounces — we only need
  // sub-frame accuracy and rAF gives us "next paint" granularity for free.
  useEffect(() => {
    let raf: number | null = null;
    const onScroll = () => {
      if (raf !== null) cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        setOverviewScroll(regionId, window.scrollY);
      });
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
      if (raf !== null) cancelAnimationFrame(raf);
    };
  }, [regionId]);
}
